// Curated free photos of Panama for the public gallery/portfolio.
//
// 100% Wikimedia Commons (CC BY-SA / Public Domain), all URLs verified
// via the Wikipedia REST API page summaries on 2026-05-13. Each photo
// is the lead image from the matching Wikipedia article — so the file
// name AND the content are guaranteed to be the labelled subject.
//
// Why we dropped Unsplash : random hex IDs like `1518181867434-…` have
// no semantic link to "panama". Several IDs in the previous file
// resolved to generic stock photos (mountains, beaches, monkeys) that
// happened to look tropical but weren't actually Panama.
//
// Adding a photo :
//   1. Search "<subject> Wikipedia" → open the EN Wikipedia article
//   2. Fetch the page summary REST API :
//      https://en.wikipedia.org/api/rest_v1/page/summary/<Article_Slug>
//   3. Use the `originalimage.source` URL as `src`
//   4. Append { id, src, thumb, alt, photographer, source, sourceUrl, theme, city }
//
// `wm(path)` returns the full upload.wikimedia.org URL.
// `wmThumb(path, w)` returns the resized thumbnail variant.

export const PANAMA_THEMES = [
  { id: 'all', labelFR: 'Tout', labelEN: 'All', labelES: 'Todo' },
  { id: 'city', labelFR: 'Panama City', labelEN: 'Panama City', labelES: 'Ciudad de Panamá' },
  { id: 'casco', labelFR: 'Casco Viejo', labelEN: 'Casco Viejo', labelES: 'Casco Viejo' },
  { id: 'canal', labelFR: 'Canal', labelEN: 'Canal', labelES: 'Canal' },
  { id: 'beaches', labelFR: 'Plages', labelEN: 'Beaches', labelES: 'Playas' },
  { id: 'jungle', labelFR: 'Jungle', labelEN: 'Jungle', labelES: 'Selva' },
  { id: 'mountains', labelFR: 'Montagnes', labelEN: 'Mountains', labelES: 'Montañas' },
  { id: 'culture', labelFR: 'Culture', labelEN: 'Culture', labelES: 'Cultura' },
];

// Wikimedia Commons direct CDN URL (always live, no API key, no expiry).
const wm = (path) => `https://upload.wikimedia.org/wikipedia/commons/${path}`;

// Thumb variant (path must include the .jpg/.JPG/.png extension at the
// end since Wikimedia derives the thumb name from it). Width param is
// just a hint — the CDN serves the closest cached width.
const wmThumb = (path, width = 800) => {
  const parts = path.split('/');
  const file = parts.slice(-1)[0];
  return `https://upload.wikimedia.org/wikipedia/commons/thumb/${path}/${width}px-${file}`;
};

export const PANAMA_PHOTOS = [
  // ─── PANAMA CITY ───
  {
    id: 'pc-cinta-costera',
    src: wm('9/95/Cinta_Costera_Panam%C3%A1.jpg'),
    thumb: wm('9/95/Cinta_Costera_Panam%C3%A1.jpg'),
    alt: 'Cinta Costera, promenade et skyline de Panama City',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Cinta_Costera_Panam%C3%A1.jpg',
    theme: 'city',
    city: 'Panama City',
  },
  {
    id: 'pc-causeway-islands',
    src: wmThumb('9/99/Isla_Naos_e_Isla_Perico.jpg', 1600),
    thumb: wmThumb('9/99/Isla_Naos_e_Isla_Perico.jpg', 600),
    alt: 'Îles Naos et Perico, vue de la Calzada de Amador',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Isla_Naos_e_Isla_Perico.jpg',
    theme: 'city',
    city: 'Panama City',
  },
  {
    id: 'pc-ancon-flag',
    src: wm('6/6a/Bandera_de_Panam%C3%A1_en_el_Cerro_Anc%C3%B3n.jpg'),
    thumb: wm('6/6a/Bandera_de_Panam%C3%A1_en_el_Cerro_Anc%C3%B3n.jpg'),
    alt: 'Drapeau panaméen géant au sommet du Cerro Ancón',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Bandera_de_Panam%C3%A1_en_el_Cerro_Anc%C3%B3n.jpg',
    theme: 'city',
    city: 'Panama City',
  },
  {
    id: 'pc-biomuseo',
    src: wmThumb('d/d6/Biomuseo_rear_stereo_pair_R.agr_%28cropped%29.jpg', 1600),
    thumb: wmThumb('d/d6/Biomuseo_rear_stereo_pair_R.agr_%28cropped%29.jpg', 600),
    alt: 'BioMuseo, musée de la biodiversité conçu par Frank Gehry',
    photographer: 'R. Agr / Wikimedia',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Biomuseo_rear_stereo_pair_R.agr_(cropped).jpg',
    theme: 'city',
    city: 'Panama City',
  },
  {
    id: 'pc-panama-viejo',
    src: wm('0/0e/Torre_de_panama_viejo.jpg'),
    thumb: wm('0/0e/Torre_de_panama_viejo.jpg'),
    alt: 'Tour de Panamá Viejo, ruines de la cité originelle (1519-1671)',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Torre_de_panama_viejo.jpg',
    theme: 'city',
    city: 'Panama City',
  },

  // ─── CASCO VIEJO ───
  {
    id: 'casco-aerial',
    src: wmThumb('1/17/Casco_Antiguo_%28San_Felipe%29_-_Pamama.jpg', 1600),
    thumb: wmThumb('1/17/Casco_Antiguo_%28San_Felipe%29_-_Pamama.jpg', 600),
    alt: 'Vue aérienne de Casco Antiguo, péninsule de San Felipe',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Casco_Antiguo_(San_Felipe)_-_Pamama.jpg',
    theme: 'casco',
    city: 'Panama City',
  },

  // ─── CANAL ───
  {
    id: 'canal-bridge-americas',
    src: wm('8/80/US_Navy_080813-N-6266K-036_American_and_Panamanian_security_forces_practice_water_steering_and_maneuvering-edit.jpg'),
    thumb: wm('8/80/US_Navy_080813-N-6266K-036_American_and_Panamanian_security_forces_practice_water_steering_and_maneuvering-edit.jpg'),
    alt: 'Pont des Amériques au-dessus du canal de Panama',
    photographer: 'US Navy / Wikimedia',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:US_Navy_080813-N-6266K-036_American_and_Panamanian_security_forces_practice_water_steering_and_maneuvering-edit.jpg',
    theme: 'canal',
    city: 'Panama City',
  },

  // ─── BEACHES / SAN BLAS / BOCAS ───
  {
    id: 'sanblas-islands',
    src: wm('0/0a/San_Blas_Islands.jpg'),
    thumb: wm('0/0a/San_Blas_Islands.jpg'),
    alt: 'Archipel des San Blas (Guna Yala), îles paradisiaques',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:San_Blas_Islands.jpg',
    theme: 'beaches',
    city: 'Guna Yala',
  },
  {
    id: 'sanblas-kuna-beach',
    src: wm('f/f2/Kuna_Yala_beach_2003.jpg'),
    thumb: wm('f/f2/Kuna_Yala_beach_2003.jpg'),
    alt: 'Plage de Guna Yala (San Blas), comarque indigène kuna',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Kuna_Yala_beach_2003.jpg',
    theme: 'beaches',
    city: 'Guna Yala',
  },
  {
    id: 'bocas-shore',
    src: wm('b/b8/Bocas2.jpg'),
    thumb: wm('b/b8/Bocas2.jpg'),
    alt: 'Côte de Bocas del Toro, province du nord-ouest',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Bocas2.jpg',
    theme: 'beaches',
    city: 'Bocas del Toro',
  },

  // ─── JUNGLE / NATURE ───
  {
    id: 'soberania-rainforest',
    src: wmThumb('9/9e/Flickr_-_ggallice_-_Rainforest_%281%29.jpg', 1600),
    thumb: wmThumb('9/9e/Flickr_-_ggallice_-_Rainforest_%281%29.jpg', 600),
    alt: 'Forêt tropicale du parc national Soberanía',
    photographer: 'Geoff Gallice / Wikimedia',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Flickr_-_ggallice_-_Rainforest_(1).jpg',
    theme: 'jungle',
    city: 'Soberanía',
  },
  {
    id: 'jungle-howler-panamanian',
    src: wmThumb('8/80/Panamanian_Male_Adult_Howler_Monkey.jpg', 1600),
    thumb: wmThumb('8/80/Panamanian_Male_Adult_Howler_Monkey.jpg', 600),
    alt: 'Singe hurleur mâle adulte panaméen (Alouatta palliata)',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Panamanian_Male_Adult_Howler_Monkey.jpg',
    theme: 'jungle',
    city: 'Panama',
  },

  // ─── MOUNTAINS ───
  {
    id: 'volcan-baru',
    src: wm('7/7a/Volcan_baru.jpg'),
    thumb: wm('7/7a/Volcan_baru.jpg'),
    alt: 'Volcan Barú vu depuis les hautes terres de Chiriquí',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Volcan_baru.jpg',
    theme: 'mountains',
    city: 'Chiriquí',
  },
  {
    id: 'volcan-baru-2',
    src: wm('4/44/Volcanbaru1.jpg'),
    thumb: wm('4/44/Volcanbaru1.jpg'),
    alt: 'Volcán Barú, point culminant du Panama (3 475 m)',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Volcanbaru1.jpg',
    theme: 'mountains',
    city: 'Chiriquí',
  },
  {
    id: 'boquete-valley',
    src: wm('2/29/Boquete.jpg'),
    thumb: wm('2/29/Boquete.jpg'),
    alt: 'Vallée de Boquete, région de Chiriquí',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Boquete.jpg',
    theme: 'mountains',
    city: 'Boquete',
  },
  {
    id: 'geisha-coffee',
    src: wm('7/79/Coffee_Farm.jpg'),
    thumb: wm('7/79/Coffee_Farm.jpg'),
    alt: 'Plantation de café Geisha, fierté du terroir panaméen',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:Coffee_Farm.jpg',
    theme: 'mountains',
    city: 'Boquete',
  },

  // ─── CULTURE ───
  {
    id: 'pollera-panamenian',
    src: wm('d/d4/PanamenianPollera.JPG'),
    thumb: wm('d/d4/PanamenianPollera.JPG'),
    alt: 'Pollera, robe traditionnelle panaméenne',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:PanamenianPollera.JPG',
    theme: 'culture',
    city: 'Las Tablas',
  },
  {
    id: 'mola-kuna',
    src: wmThumb('9/99/KunaWomanSellingMolas.jpg', 1600),
    thumb: wmThumb('9/99/KunaWomanSellingMolas.jpg', 600),
    alt: 'Femme kuna vendant des molas traditionnelles',
    photographer: 'Wikimedia Commons',
    source: 'Wikimedia',
    sourceUrl: 'https://commons.wikimedia.org/wiki/File:KunaWomanSellingMolas.jpg',
    theme: 'culture',
    city: 'San Blas',
  },
];
